import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Typography,
  Link as MUILink,
} from '@mui/material';
import { Home, Inventory2, Warning as WarningIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { iarService } from '@/services/iar/IARService';
import type { IARRunSummary } from '@/types/iar';
import { getErrorMessage } from '@/utils/httpClient';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTableCell } from '@/components/shared/SortableTableCell';

const formatDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('en-US');
};

export const IARPage: React.FC = () => {
  const navigate = useNavigate();
  const isViewer = useWriteGuard();
  const [runs, setRuns] = useState<IARRunSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStartRunModalOpen, setIsStartRunModalOpen] = useState(false);
  const [isStartingRun, setIsStartingRun] = useState(false);
  const [isCancellingRun, setIsCancellingRun] = useState(false);
  const startRunInFlightRef = useRef(false);
  const [startRunErrorMessage, setStartRunErrorMessage] = useState<string | null>(null);
  const [runPendingCancel, setRunPendingCancel] = useState<IARRunSummary | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  usePageTitle('IAR Dashboard');

  const loadRuns = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);

    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const nextRuns = await iarService.listRuns();
        setRuns(nextRuns);
        setIsLoading(false);
        return;
      } catch (error) {
        if (attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          continue;
        }
        console.error('Failed to load IAR runs', error);
        setErrorMessage('Unable to load IAR runs. Please try again.');
      }
    }

    setIsLoading(false);
  }, []);

  const handleRetryLoad = useCallback(() => {
    void loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  const hasInProgressRun = useMemo(() => runs.some((run) => run.status === 'In Progress'), [runs]);
  const { sortField, sortDirection, onSort, sortedRows: sortedRuns } = useTableSort(
    runs as unknown as Record<string, unknown>[],
  );

  const handleOpenStartRunModal = useCallback(() => {
    setStartRunErrorMessage(null);
    setIsStartRunModalOpen(true);
  }, []);

  const handleCloseStartRunModal = useCallback(() => {
    if (isStartingRun) {
      return;
    }

    setIsStartRunModalOpen(false);
    setStartRunErrorMessage(null);
  }, [isStartingRun]);

  const handleStartRun = useCallback(async () => {
    if (startRunInFlightRef.current) {
      return;
    }

    startRunInFlightRef.current = true;
    setIsStartingRun(true);
    setStartRunErrorMessage(null);

    try {
      const createdRun = await iarService.initiateRun();
      setIsStartRunModalOpen(false);
      navigate(`/iar/${createdRun.runId}`);
    } catch (error) {
      console.error('Failed to initiate IAR run', error);
      const normalizedErrorMessage = getErrorMessage(error).toLowerCase();

      if (normalizedErrorMessage.includes('in progress')) {
        setStartRunErrorMessage(
          'An In Progress run already exists. Open that run from the dashboard.',
        );
      } else if (
        normalizedErrorMessage.includes('no stock') ||
        normalizedErrorMessage.includes('empty snapshot')
      ) {
        setStartRunErrorMessage('No stock available at true locations. Check your inventory.');
      } else {
        setStartRunErrorMessage('Unable to start an IAR run.');
      }
    } finally {
      startRunInFlightRef.current = false;
      setIsStartingRun(false);
      await loadRuns();
    }
  }, [loadRuns, navigate]);

  const navigateToRun = useCallback(
    (runId: string) => {
      navigate(`/iar/${runId}`);
    },
    [navigate],
  );

  const handleOpenCancelRunModal = useCallback((run: IARRunSummary) => {
    setErrorMessage(null);
    setRunPendingCancel(run);
  }, []);

  const handleCloseCancelRunModal = useCallback(() => {
    if (isCancellingRun) {
      return;
    }

    setRunPendingCancel(null);
  }, [isCancellingRun]);

  const handleConfirmCancelRun = useCallback(async () => {
    if (!runPendingCancel) {
      return;
    }

    setIsCancellingRun(true);
    setErrorMessage(null);

    try {
      await iarService.cancelRun(runPendingCancel.runId);
      setRunPendingCancel(null);
      await loadRuns();
    } catch (error) {
      console.error('Failed to cancel IAR run', error);
      setErrorMessage(`Unable to cancel IAR run. ${getErrorMessage(error)}`);
    } finally {
      setIsCancellingRun(false);
    }
  }, [loadRuns, runPendingCancel]);

  return (
    <Container maxWidth={false} sx={{ mt: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MUILink
            color="inherit"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </MUILink>
          <Typography color="text.primary">IAR Dashboard</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Inventory2 color="primary" />
          <Typography component="h1" variant="h4">
            IAR Dashboard
          </Typography>
        </Box>
      </Box>

      {!isViewer && (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          onClick={handleOpenStartRunModal}
          disabled={hasInProgressRun || isLoading}
        >
          Initiate IAR
        </Button>
      </Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTableCell field="runNumber" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Run #</SortableTableCell>
              <SortableTableCell field="runDate" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Date</SortableTableCell>
              <SortableTableCell field="status" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Status</SortableTableCell>
              <SortableTableCell field="inventoryQty" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="right">Inventory Qty</SortableTableCell>
              <TableCell>Resume</TableCell>
              {!isViewer && <TableCell>Cancel</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {(sortedRuns as unknown as IARRunSummary[]).map((run) => {
              const canMutate = run.status === 'In Progress';
              const isCompleting = run.status === 'Completing';
              const isReadOnly = run.status === 'Complete' || run.status === 'Cancelled';

              return (
                <TableRow
                  key={run.runId}
                  hover
                  sx={{ cursor: isReadOnly ? 'pointer' : 'default' }}
                  onClick={() => {
                    if (isReadOnly) {
                      navigateToRun(run.runId);
                    }
                  }}
                >
                  <TableCell>{run.runNumber}</TableCell>
                  <TableCell>{formatDate(run.runDate)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={run.status} />
                  </TableCell>
                  <TableCell align="right">{run.inventoryQty}</TableCell>
                  <TableCell>
                    {canMutate ? (
                      <Button size="small" onClick={() => navigateToRun(run.runId)}>
                        Resume
                      </Button>
                    ) : isCompleting ? (
                      <Button size="small" color="warning" onClick={() => navigateToRun(run.runId)}>
                        View Progress
                      </Button>
                    ) : isReadOnly ? (
                      <Button size="small" onClick={() => navigateToRun(run.runId)}>
                        View (read-only)
                      </Button>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  {!isViewer && (
                  <TableCell>
                    {canMutate ? (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleOpenCancelRunModal(run)}
                        disabled={isCancellingRun}
                      >
                        Cancel
                      </Button>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  )}
                </TableRow>
              );
            })}
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary">Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : runs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary">No IAR runs found.</Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      {errorMessage ? (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetryLoad} disabled={isLoading}>
              Retry
            </Button>
          }
        >
          {errorMessage}
        </Alert>
      ) : null}

      <Dialog
        open={isStartRunModalOpen}
        onClose={isStartingRun ? undefined : handleCloseStartRunModal}
        fullWidth
        maxWidth="sm"
        aria-labelledby="iar-start-run-dialog-title"
      >
        <DialogTitle id="iar-start-run-dialog-title">Start New IAR Run</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>
              This will create a new Inventory Adjustment Report (IAR) run by capturing the current
              stock quantities for all items at true locations.
            </Typography>

            <Typography color="text.secondary">
              Once started, you can record the physical inventory counts and identify any
              discrepancies (gains or losses).
            </Typography>

            {startRunErrorMessage ? <Alert severity="error">{startRunErrorMessage}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStartRunModal} disabled={isStartingRun} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleStartRun()}
            disabled={isStartingRun}
            sx={{ '&.Mui-disabled': { pointerEvents: 'auto' } }}
          >
            {isStartingRun ? 'Starting...' : 'Start Run'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(runPendingCancel)}
        onClose={isCancellingRun ? undefined : handleCloseCancelRunModal}
        maxWidth="xs"
        aria-labelledby="iar-cancel-run-confirmation-title"
        aria-describedby="iar-cancel-run-confirmation-description"
      >
        <DialogTitle
          id="iar-cancel-run-confirmation-title"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <WarningIcon color="warning" />
          Cancel IAR Run
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="iar-cancel-run-confirmation-description">
            Cancel IAR run #{runPendingCancel?.runNumber ?? '-'}? This will mark the run as
            cancelled and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelRunModal} color="inherit" disabled={isCancellingRun}>
            Keep Run
          </Button>
          <Button
            onClick={() => void handleConfirmCancelRun()}
            color="warning"
            variant="contained"
            autoFocus
            disabled={isCancellingRun}
          >
            {isCancellingRun ? 'Cancelling...' : 'Yes, Cancel IAR Run'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
